import { useDeferredValue, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, createIdempotencyKey, isPreviewMode } from "../api/client";
import type { AccountListDetail, AccountListSummary, AccountSummary, ProxyImportResult, ProxyItem, SubBrokenAccountScan, SubConnection, TaskCreateInput, TaskCreateResult } from "../api/types";
import { useAuth } from "../context/AuthContext";
import { useTaskFeed } from "../context/TaskFeedContext";
import { useToast } from "../context/ToastContext";
import { ConfirmDialog } from "./ConfirmDialog";
import { EmptyState } from "./EmptyState";
import { Icon } from "./Icon";
import { ProxyImportDialog } from "./IntegrationDialogs";
import { Modal } from "./Modal";
import { LoadingRows } from "./ResourceNotice";
import { StatusPill } from "./StatusPill";

interface TaskWizardProps {
  open: boolean;
  initialListId?: number | null;
  onClose: () => void;
  onCreated?: (task: TaskCreateResult) => void;
}

const steps = ["选择账号", "执行方式", "集成路径", "确认提交"];
const eligibleStatuses = new Set(["active", "success", "failed"]);
const MAX_TASK_ITEMS = 200;

function visibleAccount(account: AccountSummary) {
  return eligibleStatuses.has(account.status) && account.status !== "running";
}

export function TaskWizard({ open, initialListId, onClose, onCreated }: TaskWizardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const concurrencyLimit = Math.max(1, Number(user?.task_concurrency_limit || 1));
  const feed = useTaskFeed();
  const { pushToast } = useToast();
  const [step, setStep] = useState(0);
  const [lists, setLists] = useState<AccountListSummary[]>([]);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AccountListDetail | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [query, setQuery] = useState("");
  const [failedOnly, setFailedOnly] = useState(false);
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());
  const [concurrency, setConcurrency] = useState(1);
  const [connections, setConnections] = useState<SubConnection[]>([]);
  const [proxies, setProxies] = useState<ProxyItem[]>([]);
  const [subConnectionId, setSubConnectionId] = useState<number | null>(null);
  const [autoRefill, setAutoRefill] = useState(true);
  const [proxyMode, setProxyMode] = useState<TaskCreateInput["proxy_mode"]>("round_robin");
  const [proxyId, setProxyId] = useState<number | null>(null);
  const [proxyImportOpen, setProxyImportOpen] = useState(false);
  const [brokenScan, setBrokenScan] = useState<SubBrokenAccountScan | null>(null);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [matchError, setMatchError] = useState("");
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [integrationError, setIntegrationError] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");

  function reset() {
    setStep(0);
    setLists([]);
    setSelectedListId(null);
    setDetail(null);
    setSelectedIds([]);
    setQuery("");
    setFailedOnly(false);
    setConcurrency(concurrencyLimit);
    setConnections([]);
    setProxies([]);
    setSubConnectionId(null);
    setAutoRefill(true);
    setProxyMode("round_robin");
    setProxyId(null);
    setProxyImportOpen(false);
    setBrokenScan(null);
    setLoadingMatches(false);
    setMatchError("");
    setLoadingLists(false);
    setLoadingDetail(false);
    setLoadingIntegrations(false);
    setBusy(false);
    setError("");
    setIntegrationError("");
    setDiscardOpen(false);
    setIdempotencyKey("");
  }

  function invalidateSubmission() {
    setIdempotencyKey("");
    setError("");
  }

  useEffect(() => {
    if (!open) return;
    let active = true;
    setConcurrency(concurrencyLimit);
    setProxyMode("round_robin");
    setProxyId(null);
    setLoadingLists(true);
    setLoadingIntegrations(true);
    setError("");
    setIntegrationError("");
    api.accountLists.list()
      .then((result) => {
        if (!active) return;
        const nextLists = result.items || [];
        setLists(nextLists);
        const preferred = initialListId && nextLists.some((item) => item.id === initialListId)
          ? initialListId
          : nextLists[0]?.id || null;
        setSelectedListId(preferred);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "账号批次加载失败");
      })
      .finally(() => { if (active) setLoadingLists(false); });

    Promise.allSettled([api.integrations.connections(), api.integrations.proxies()])
      .then(([connectionResult, proxyResult]) => {
        if (!active) return;
        const errors: string[] = [];
        if (connectionResult.status === "fulfilled") setConnections((connectionResult.value.items || []).filter((item) => item.status === "active"));
        else errors.push(connectionResult.reason instanceof Error ? `Sub2：${connectionResult.reason.message}` : "Sub2 配置加载失败");
        if (proxyResult.status === "fulfilled") {
          const activeProxies = (proxyResult.value.items || []).filter((item) => item.status === "active");
          setProxies(activeProxies);
          setConcurrency(Math.max(1, Math.min(concurrencyLimit, activeProxies.length || 1)));
        }
        else errors.push(proxyResult.reason instanceof Error ? `代理：${proxyResult.reason.message}` : "代理配置加载失败");
        setIntegrationError(errors.join("；"));
      })
      .finally(() => { if (active) setLoadingIntegrations(false); });
    return () => { active = false; };
  }, [concurrencyLimit, initialListId, open]);

  useEffect(() => {
    if (!open || !selectedListId) {
      setDetail(null);
      setSelectedIds([]);
      return;
    }
    let active = true;
    setLoadingDetail(true);
    setError("");
    api.accountLists.detail(selectedListId)
      .then((result) => {
        if (!active) return;
        setDetail(result);
        setSelectedIds((result.accounts || []).filter((account) => account.status === "active" || account.status === "failed").slice(0, MAX_TASK_ITEMS).map((account) => account.id));
        setIdempotencyKey("");
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setDetail(null);
        setSelectedIds([]);
        setError(reason instanceof Error ? reason.message : "账号详情加载失败");
      })
      .finally(() => { if (active) setLoadingDetail(false); });
    return () => { active = false; };
  }, [open, selectedListId]);

  useEffect(() => {
    if (!open || !subConnectionId) {
      setBrokenScan(null);
      setLoadingMatches(false);
      setMatchError("");
      return;
    }
    let active = true;
    setLoadingMatches(true);
    setBrokenScan(null);
    setMatchError("");
    api.integrations.brokenAccounts(subConnectionId)
      .then((result) => { if (active) setBrokenScan(result); })
      .catch((reason: unknown) => { if (active) setMatchError(reason instanceof Error ? reason.message : "Sub2 账号匹配失败"); })
      .finally(() => { if (active) setLoadingMatches(false); });
    return () => { active = false; };
  }, [open, subConnectionId]);

  async function onProxyImported(result: ProxyImportResult, replaced: boolean) {
    setLoadingIntegrations(true);
    setIntegrationError("");
    try {
      const response = await api.integrations.proxies();
      const activeProxies = (response.items || []).filter((item) => item.status === "active");
      setProxies(activeProxies);
      setProxyMode("round_robin");
      setProxyId(null);
      setConcurrency(Math.max(1, Math.min(concurrencyLimit, activeProxies.length || 1)));
      invalidateSubmission();
      pushToast({
        tone: result.imported ? "success" : "info",
        title: result.imported ? `已添加 ${result.imported} 条代理` : "没有代理被写入",
        message: result.imported
          ? `${replaced ? "代理池已替换" : "代理已加入当前用户的代理池"}${result.invalid.length ? `，${result.invalid.length} 行未写入` : ""}。`
          : `请检查弹窗中的 ${result.invalid.length} 行诊断后重新输入。`,
      });
    } catch (reason) {
      setIntegrationError(reason instanceof Error ? reason.message : "代理保存成功，但重新加载失败");
    } finally {
      setLoadingIntegrations(false);
    }
  }

  const selectableAccounts = (detail?.accounts || []).filter(visibleAccount);
  const filteredAccounts = selectableAccounts.filter((account) => {
    if (failedOnly && account.status !== "failed") return false;
    if (!deferredQuery) return true;
    return account.email.toLowerCase().includes(deferredQuery) || account.login_identity.toLowerCase().includes(deferredQuery) || String(account.id).includes(deferredQuery);
  });
  const selectedSet = new Set(selectedIds);
  const selectedAccounts = selectableAccounts.filter((account) => selectedSet.has(account.id));
  const selectedFailed = selectedAccounts.filter((account) => account.status === "failed").length;
  const selectedConnection = connections.find((connection) => connection.id === subConnectionId);
  const selectedProxy = proxies.find((proxy) => proxy.id === proxyId);
  const subAccountLinks = Object.fromEntries(
    (brokenScan?.matched || [])
      .filter((match) => match.local_account && selectedSet.has(match.local_account.id))
      .map((match) => [String(match.local_account!.id), match.sub_account_id]),
  );
  const matchedSelectedCount = Object.keys(subAccountLinks).length;

  function toggleAccount(id: number) {
    if (!selectedIds.includes(id) && selectedIds.length >= MAX_TASK_ITEMS) {
      setError(`单个任务最多选择 ${MAX_TASK_ITEMS} 个账号，请拆分批次执行。`);
      return;
    }
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
    invalidateSubmission();
  }

  function setVisibleSelection(select: boolean) {
    const visibleIds = filteredAccounts.map((account) => account.id);
    const combined = Array.from(new Set([...selectedIds, ...visibleIds]));
    setSelectedIds(select ? combined.slice(0, MAX_TASK_ITEMS) : selectedIds.filter((id) => !visibleIds.includes(id)));
    invalidateSubmission();
    if (select && combined.length > MAX_TASK_ITEMS) setError(`已选到上限 ${MAX_TASK_ITEMS} 个，其余账号请另建任务。`);
  }

  function validateStep(targetStep: number) {
    if (targetStep > 0 && (!selectedListId || !selectedIds.length)) {
      setError("请至少选择一个可执行账号。");
      return false;
    }
    if (targetStep > 2 && proxyMode === "single" && !proxyId) {
      setError("单代理模式需要选择一个代理。");
      return false;
    }
    if (targetStep > 2 && proxyMode !== "none" && !proxies.length) {
      setError("默认不使用服务器网络，请先输入至少一条代理，或主动选择服务器直连。");
      return false;
    }
    if (targetStep > 2 && subConnectionId && autoRefill && (loadingMatches || matchedSelectedCount === 0)) {
      setError(loadingMatches ? "正在匹配 Sub2 异常账号，请等待扫描完成。" : "所选账号与 Sub2 异常账号没有匹配项，无法开启自动回填。");
      return false;
    }
    setError("");
    return true;
  }

  function goNext() {
    const next = Math.min(3, step + 1);
    if (validateStep(next)) setStep(next);
  }

  function requestClose() {
    if (busy) return;
    if (step > 0 || selectedIds.length) {
      setDiscardOpen(true);
      return;
    }
    reset();
    onClose();
  }

  function closeNow() {
    reset();
    onClose();
  }

  async function submitTask() {
    if (!selectedListId || !selectedIds.length || !validateStep(4)) return;
    const key = idempotencyKey || createIdempotencyKey("task-create");
    if (!idempotencyKey) setIdempotencyKey(key);
    setBusy(true);
    setError("");
    try {
      const result = await api.tasks.create({
        account_ids: selectedIds,
        list_id: selectedListId,
        sub_connection_id: subConnectionId,
        sub_account_links: subAccountLinks,
        auto_refill_sub2: Boolean(subConnectionId && autoRefill),
        proxy_mode: proxyMode,
        proxy_id: proxyMode === "single" ? proxyId : null,
        options: { concurrency },
      }, key);
      pushToast({ tone: "success", title: `任务 #${result.task_id} 已进入队列`, message: `服务端已接收 ${result.total} 个账号，页面将持续恢复进度。` });
      feed.refresh();
      onCreated?.(result);
      reset();
      onClose();
      navigate(`/tasks/${result.task_id}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "任务提交失败，请使用同一页面重试。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open={open && !discardOpen && !proxyImportOpen}
        wide
        busy={busy}
        eyebrow="TASK COMPOSER"
        title="创建 OAuth 任务"
        description="四步确认账号范围、执行强度和结果去向，提交后由服务端持续执行。"
        onClose={requestClose}
      >
        <ol className="flow-stepper flow-stepper--four" aria-label="任务创建进度">
          {steps.map((label, index) => (
            <li key={label} className={index < step ? "is-done" : index === step ? "is-current" : ""} aria-current={index === step ? "step" : undefined}>
              <button type="button" onClick={() => { if (index < step) setStep(index); }} disabled={index >= step || busy} aria-label={`返回步骤：${label}`}>
                <span>{index < step ? <Icon name="check" size={15} /> : index + 1}</span><b>{label}</b>
              </button>
            </li>
          ))}
        </ol>

        {step === 0 ? (
          <div className="flow-panel task-account-step">
            <label className="flow-field">
              <span>账号批次</span>
              <select
                data-autofocus
                value={selectedListId || ""}
                disabled={loadingLists}
                onChange={(event) => { setSelectedListId(Number(event.target.value) || null); invalidateSubmission(); }}
              >
                {!lists.length ? <option value="">{loadingLists ? "正在加载批次" : "没有可用批次"}</option> : null}
                {lists.map((list) => <option key={list.id} value={list.id}>{list.name} · {list.account_count || 0} 个账号</option>)}
              </select>
            </label>
            {loadingLists || loadingDetail ? <LoadingRows count={5} /> : null}
            {!loadingLists && !lists.length ? <EmptyState compact icon="accounts" title="还没有账号批次" description="先完成 JSONL 导入，服务端确认保存后才能创建任务。" /> : null}
            {detail && !loadingDetail ? (
              <>
                <div className="account-picker-tools">
                  <label className="search-field"><Icon name="search" size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索邮箱或账号编号" /></label>
                  <label className="check-control"><input type="checkbox" checked={failedOnly} onChange={(event) => setFailedOnly(event.target.checked)} /><span>只看上次失败</span></label>
                  <button type="button" className="button button--quiet button--small" onClick={() => setVisibleSelection(true)}>全选当前结果</button>
                  <button type="button" className="button button--quiet button--small" onClick={() => setVisibleSelection(false)}>清空当前结果</button>
                </div>
                <div className="account-picker-summary"><span><b>{selectedIds.length}/{MAX_TASK_ITEMS}</b> 已选择</span><span><b>{selectedFailed}</b> 上次失败</span><span><b>{selectableAccounts.length}</b> 可执行</span></div>
                {!filteredAccounts.length ? <EmptyState compact icon="search" title="当前筛选没有账号" description="清空搜索词或关闭“只看上次失败”。" /> : (
                  <div className="account-picker-list" role="group" aria-label="选择任务账号">
                    {filteredAccounts.slice(0, 250).map((account) => (
                      <label key={account.id} className={`account-picker-row${selectedSet.has(account.id) ? " is-selected" : ""}`}>
                        <input type="checkbox" checked={selectedSet.has(account.id)} onChange={() => toggleAccount(account.id)} />
                        <span className="account-picker-row__identity"><b>{account.email || account.login_identity}</b><small>#{account.id} · {account.mailbox_url_masked || "邮箱接口已保存"}</small></span>
                        <StatusPill status={account.status} />
                        <small className="account-picker-row__error">{account.last_error || "无待处理诊断"}</small>
                      </label>
                    ))}
                    {filteredAccounts.length > 250 ? <p className="list-limit-note">为保证滚动流畅，仅展示前 250 条；搜索作用于全部结果，批量选择最多取前 {MAX_TASK_ITEMS} 条。</p> : null}
                  </div>
                )}
              </>
            ) : null}
          </div>
        ) : null}

        {step === 1 ? (
          <div className="flow-panel execution-step">
            <fieldset className="choice-fieldset"><legend>任务类型</legend><label className="choice-card is-selected"><input type="radio" checked readOnly /><span><Icon name="refresh" size={22} /></span><div><b>OAuth 重授权</b><small>重新获取授权结果并写回所选账号，仅成功项计费。</small></div><em>当前可用</em></label></fieldset>
            <label className="range-field">
              <span><b>期望并发</b><small>服务端会按账号数量和可用代理自动收敛。</small></span>
              <output>{concurrency}</output>
              <input type="range" min={1} max={concurrencyLimit} step={1} value={concurrency} disabled={concurrencyLimit === 1} onChange={(event) => { setConcurrency(Number(event.target.value)); invalidateSubmission(); }} />
              <span className="range-field__ends"><small>1 · 稳健</small><small>{concurrencyLimit} · 上限</small></span>
            </label>
            <div className="execution-estimate"><span><Icon name="clock" size={19} /></span><div><b>预计分为 {Math.max(1, Math.ceil(selectedIds.length / concurrency))} 个并发轮次</b><small>实际耗时取决于 OAuth、邮箱接口和代理响应，不承诺固定完成时间。</small></div></div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="flow-panel integration-step">
            {loadingIntegrations ? <LoadingRows count={4} /> : null}
            {integrationError ? <div className="inline-notice inline-notice--warning"><Icon name="alert" size={18} /><span>{integrationError}。代理加载失败时不会自动改用服务器网络。</span></div> : null}
            <div className="integration-choice-grid">
              <label className="flow-field"><span>结果写入 Sub2</span><select value={subConnectionId || ""} onChange={(event) => { setSubConnectionId(Number(event.target.value) || null); invalidateSubmission(); }}><option value="">不写入 Sub2</option>{connections.map((connection) => <option key={connection.id} value={connection.id}>{connection.name || `连接 #${connection.id}`}</option>)}</select></label>
              <label className={`check-control check-control--panel${!subConnectionId ? " is-disabled" : ""}`}><input type="checkbox" checked={autoRefill} disabled={!subConnectionId} onChange={(event) => { setAutoRefill(event.target.checked); invalidateSubmission(); }} /><span><b>授权成功后自动回填</b><small>仅在选择 Sub2 连接时生效</small></span></label>
            </div>
            {subConnectionId ? <div className={`match-coverage${matchError || (!loadingMatches && autoRefill && matchedSelectedCount === 0) ? " is-warning" : ""}`}><span className={loadingMatches ? "button-spinner" : "match-coverage__signal"} /><div><b>{loadingMatches ? "正在扫描 Sub2 异常账号" : matchError ? "Sub2 匹配扫描失败" : `${matchedSelectedCount}/${selectedIds.length} 个所选账号可自动回填`}</b><small>{matchError || (matchedSelectedCount < selectedIds.length ? `${selectedIds.length - matchedSelectedCount} 个账号没有 Sub2 异常记录，只执行 OAuth，不会回填。` : "所有所选账号都有明确的 Sub2 目标映射。")}</small></div></div> : null}
            <fieldset className="choice-fieldset"><legend>代理路径</legend><div className="proxy-mode-grid">
              {([
                ["round_robin", "代理池轮询", proxies.length ? `${proxies.length} 个可用代理轮换` : "需要先输入代理"],
                ["single", "固定单代理", "全部账号使用同一条路径"],
                ["none", "服务器直连", "仅在你明确需要时使用"],
              ] as const).map(([value, title, description]) => <label key={value} className={`mini-choice${proxyMode === value ? " is-selected" : ""}${value === "single" && !proxies.length ? " is-disabled" : ""}`}><input type="radio" name="proxy-mode" value={value} checked={proxyMode === value} disabled={value === "single" && !proxies.length} onChange={() => { setProxyMode(value); setConcurrency(value === "single" ? 1 : value === "round_robin" ? Math.max(1, Math.min(concurrencyLimit, proxies.length || 1)) : concurrencyLimit); if (value !== "single") setProxyId(null); invalidateSubmission(); }} /><b>{title}</b><small>{description}</small></label>)}
            </div></fieldset>
            <div className={`inline-notice task-proxy-gate${proxyMode !== "none" && !proxies.length ? " inline-notice--warning" : ""}`}>
              <Icon name={proxyMode === "none" ? "alert" : "network"} size={18} />
              <span><b>{proxyMode === "none" ? "当前明确使用服务器出口" : proxies.length ? `当前用户已有 ${proxies.length} 条代理` : "当前任务还没有可用代理"}</b><small>{proxyMode === "none" ? "系统不会自动切换到代理；返回代理池模式即可恢复非直连。" : "代理凭据由服务端加密保存，任务失败时不会回退服务器直连。"}</small></span>
              <button type="button" className="button button--quiet button--small" onClick={() => setProxyImportOpen(true)}>{proxies.length ? "继续添加" : "输入代理"}</button>
            </div>
            {proxyMode === "single" ? <label className="flow-field"><span>选择固定代理</span><select value={proxyId || ""} onChange={(event) => { setProxyId(Number(event.target.value) || null); invalidateSubmission(); }}><option value="">请选择代理</option>{proxies.map((proxy) => <option key={proxy.id} value={proxy.id}>{proxy.label || proxy.masked_url}</option>)}</select></label> : null}
          </div>
        ) : null}

        {step === 3 ? (
          <div className="flow-panel confirm-task-step">
            <div className="task-receipt">
              <div><span className="eyebrow">TASK RECEIPT</span><h3>{detail?.list.name || "账号批次"}</h3><p>确认以下服务端写入参数。提交后可离开页面，任务状态会自动恢复。</p></div>
              <span className="task-receipt__count"><b>{selectedIds.length}</b><small>ACCOUNTS</small></span>
            </div>
            <dl className="task-confirm-grid">
              <div><dt>任务类型</dt><dd>OAuth 重授权</dd></div>
              <div><dt>期望并发</dt><dd>{concurrency}</dd></div>
              <div><dt>Sub2 去向</dt><dd>{selectedConnection?.name || "不写入"}</dd></div>
              {selectedConnection && autoRefill ? <div><dt>Sub2 匹配</dt><dd>{matchedSelectedCount}/{selectedIds.length} 个账号</dd></div> : null}
              <div><dt>代理路径</dt><dd>{proxyMode === "none" ? "服务器直连（主动选择）" : proxyMode === "round_robin" ? `代理池轮询（${proxies.length}）` : selectedProxy?.label || selectedProxy?.masked_url || "尚未选择"}</dd></div>
            </dl>
            <div className={`cost-forecast${(user?.credit_balance || 0) < selectedIds.length ? " is-warning" : ""}`}><span><Icon name="credits" size={22} /></span><div><b>最多需要 {selectedIds.length} 积分</b><small>当前余额 {user?.credit_balance ?? 0}。只对成功授权项计费，失败项不扣分。</small></div></div>
            <div className="inline-notice"><Icon name="alert" size={18} /><span>提交按钮使用幂等键保护。网络中断后直接在此重试，不会创建重复任务。</span></div>
          </div>
        ) : null}

        {isPreviewMode ? <div className="flow-error" role="status">当前是只读预览环境，不能创建任务。</div> : error ? <div className="flow-error" role="alert">{error}</div> : null}

        <footer className="flow-actions">
          <button type="button" className="button button--quiet" onClick={step === 0 ? requestClose : () => { setStep((value) => Math.max(0, value - 1)); setError(""); }} disabled={busy}>{step === 0 ? "取消" : "上一步"}</button>
          {step < 3 ? <button type="button" className="button button--primary" onClick={goNext} disabled={busy || (step === 0 && (!selectedIds.length || loadingDetail))}>下一步 <Icon name="arrow" size={17} /></button> : <button type="button" className="button button--primary" onClick={() => void submitTask()} disabled={busy || isPreviewMode}>{busy ? <span className="button-spinner" /> : null}{busy ? "提交中" : `确认创建任务 · ${selectedIds.length} 项`}</button>}
        </footer>
      </Modal>

      <ProxyImportDialog open={open && proxyImportOpen} onClose={() => setProxyImportOpen(false)} onImported={(result, replaced) => void onProxyImported(result, replaced)} />

      <ConfirmDialog
        open={open && discardOpen}
        title="退出任务创建？"
        description="当前账号选择和执行参数会被清除，尚未向服务端提交任务。"
        confirmLabel="退出并清除"
        danger
        onCancel={() => setDiscardOpen(false)}
        onConfirm={closeNow}
      />
    </>
  );
}
